"use client"
import { type CSSProperties, useCallback, useRef } from "react"
import cn from "classnames"

import { AnimateText } from "~/components/elements/AnimateText/AnimateText"
import { FadeIn } from "~/components/elements/FadeIn/FadeIn"
import { Button } from "~/components/primitives/Button/Button"
import { HtmlRenderer } from "~/components/primitives/HtmlRenderer"
import { ResponsiveAsset } from "~/components/primitives/ResponsiveAsset/ResponsiveAsset"
import { useStickyHeroFade } from "~/hooks/useStickyHeroFade"
import { getModuleConfigProps } from "~/utils/moduleConfig"

import type { HomeHeroInterface } from "./HomeHero.interface"
import s from "./HomeHero.module.scss"

export type HomeHeroViewProps = Omit<HomeHeroInterface, "id"> & {
  moduleIndex: number
  className?: string
  style?: CSSProperties
}

/**
 * Presentational component for Hero module.
 * Use this for Storybook stories and testing.
 * The main Hero component is an async Server Component that fetches CMS data.
 */
export const HomeHeroView = ({
  moduleIndex,
  headline,
  asset,
  ctas,
  config,
  className,
  style,
}: HomeHeroViewProps) => {
  const configProps = getModuleConfigProps(config)
  const heroElRef = useRef<HTMLElement>(null)
  const heroRef = useCallback((node: HTMLElement | null) => {
    heroElRef.current = node
    if (node) {
      const isFirstChild = node.parentElement?.firstElementChild === node
      if (isFirstChild) {
        node.parentElement?.parentElement?.setAttribute("data-has-hero", "true")
      }
    }
  }, [])

  useStickyHeroFade(heroElRef)

  return (
    <section
      ref={heroRef}
      className={cn(
        s.hero,
        `stack-module-${moduleIndex}`,
        className,
        "homeHero",
      )}
      data-module="hero"
      {...configProps}
      style={{
        ...configProps.style,
        ...style,
      }}
    >
      {asset?.desktop && (
        <ResponsiveAsset
          className={s.backgroundAsset}
          desktop={asset.desktop}
          mobile={asset.mobile ?? undefined}
          imageSizes={{ base: "100vw" }}
        />
      )}
      <div className={cn("container", "default-grid")}>
        <div className={s.heroContainer}>
          <header>
            {headline && (
              <AnimateText element="h1">
                <HtmlRenderer inline html={headline} />
              </AnimateText>
            )}
          </header>
          <FadeIn element="div" className={s.content} delay={0.5}>
            {ctas && ctas.length > 0 && (
              <ul className={s.ctaList}>
                {ctas.map((cta, index) => (
                  <li key={index}>
                    <Button theme="dark" className={s.cta} cta={cta} />
                  </li>
                ))}
              </ul>
            )}
          </FadeIn>
        </div>
      </div>
    </section>
  )
}
